import { useState } from 'react';
import { Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';

export default function AdminGate({ children }) {
  const { status, login, logout } = useAdminAuth();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <p className="text-sm uppercase tracking-widest text-[#84A98C]" data-testid="admin-gate-loading">
          Unlocking…
        </p>
      </div>
    );
  }

  if (status === 'out') {
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!password) return;
      setBusy(true);
      try {
        await login(password);
        toast.success('Welcome back');
      } catch (err) {
        const msg = err.response?.data?.detail || 'Login failed';
        toast.error(typeof msg === 'string' ? msg : 'Login failed');
      } finally {
        setBusy(false);
        setPassword('');
      }
    };

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6"
        data-testid="admin-login-screen"
      >
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md p-10 bg-white rounded-sm shadow-soft"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-12 h-12 rounded-full bg-[#F5F5F0] flex items-center justify-center mb-4">
              <Lock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#84A98C] mb-2">Whispering Wristwear</p>
            <h1 className="font-serif text-2xl text-[#354F52] mb-1">Admin area</h1>
            <p className="text-sm text-[#84A98C]">Enter the admin password to continue.</p>
          </div>

          <label htmlFor="admin-password" className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            className="w-full px-3 py-2.5 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
            placeholder="••••••••••••"
            data-testid="admin-password-input"
            required
          />

          <Button
            type="submit"
            disabled={busy || !password}
            className="mt-6 w-full bg-[#52796F] hover:bg-[#354F52] text-[#FAF9F6]"
            data-testid="admin-login-submit"
          >
            {busy ? 'Unlocking…' : 'Sign in'}
          </Button>
        </form>
      </div>
    );
  }

  // status === 'in' → render the protected page + a small floating logout pill
  return (
    <div className="relative">
      {children}
      <button
        onClick={logout}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#354F52] text-[#FAF9F6] text-xs uppercase tracking-widest shadow-float hover:bg-[#52796F] transition-colors"
        data-testid="admin-logout-btn"
        title="Sign out of admin"
      >
        <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
        Sign out
      </button>
    </div>
  );
}
